import {
  Alert,
  Button,
  Col,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Slider,
  Spin,
  Tag,
  Tooltip,
  message
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NetworkLayer } from '../../../services/trm/trmServices';

// Single NetworkLayer instance shared across all mounts of this component (matches
// the pattern in sagas/trm/*).
// Lazily constructed: building a NetworkLayer at module scope runs inside the
// services/api -> keycloak -> authentication -> util -> appUtils -> crsRiskDetail
// import cycle, while apiModules/CommonPageModules is still initialising and
// `CommonModule` is not yet exported. Deferring to first use keeps the single
// instance and breaks the cycle.
let _networkLayer;
const getNetworkLayer = () => {
  if (!_networkLayer) _networkLayer = new NetworkLayer();
  return _networkLayer;
};

// FIN-12421 Item 7 — TRM Settings > FRAML Settings tab.
//
// Editable form for the six numeric knobs on `framl_decay_policy` (weights, decay
// half-life, sigmoid params, load window). The JSON multiplier maps stay read-only
// in an expander — a JSON editor for those lands in a follow-up slice.
//
// Save publishes the FULL DTO back to `PUT /tm/iad/framlDecayPolicy` (BE responds
// with the same envelope and broadcasts a config-changed Kafka event; downstream
// consolidator pipelines hot-reload their in-memory policy). Confirmation modal
// gates the PUT because a save affects production scoring behaviour.
//
// When `amls.framl.scoringEnabled = false` on the BE, GET returns 404 and the
// service module normalises to `null` — component renders the "not enabled" state.
const FramlSettings = () => {
  const [state, setState] = useState({
    loading: true,
    error: null,
    policy: null
  });
  const [saving, setSaving] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const policy = await getNetworkLayer().getFramlDecayPolicy();
        if (!cancelled) {
          setState({ loading: false, error: null, policy });
          if (policy) form.setFieldsValue(pickScalars(policy));
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            error: err?.message || 'Failed to load FRAML decay policy',
            policy: null
          });
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [form]);

  const onSubmit = (values) => {
    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmedSave = async () => {
    setConfirmVisible(false);
    setSaving(true);
    try {
      // FIN-12421 write-path fix: the BE now server-owns versioning + effective
      // window — on save it closes the previous row, INSERTs a new row with a
      // semver-patch-bumped policyVersionId + effectiveFrom = NOW, and echoes the
      // persisted DTO back in the response body. We send the merged form + the
      // preserved read-only fields (JSON multiplier maps + createdBy), then swap
      // state.policy for the BE's persisted DTO so the version-pill refreshes
      // without a full refetch.
      const merged = { ...state.policy, ...pendingValues };
      const response = await getNetworkLayer().putFramlDecayPolicy({
        policy: merged
      });
      setState((s) => ({
        ...s,
        policy: parsePersistedPolicy(response) || merged
      }));
      message.success(
        'FRAML settings saved. Downstream pipelines will hot-reload.'
      );
    } catch (err) {
      message.error(err?.message || 'Failed to save FRAML settings');
    } finally {
      setSaving(false);
    }
  };

  // BE returns `Ok(persistedDto.asJson.noSpaces)` — a JSON-string body via
  // Scalatra's default text-content-type. axios keeps that as a string in
  // response.data, so we JSON.parse it here. Any parse failure just falls back to
  // the pre-save merged DTO (the save still succeeded server-side).
  function parsePersistedPolicy(response) {
    const data = response?.data ?? response;
    if (!data) return null;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (_e) {
        return null;
      }
    }
    return typeof data === 'object' ? data : null;
  }

  if (state.loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Could not load FRAML settings"
          description={state.error}
          showIcon
        />
      </div>
    );
  }

  if (!state.policy) {
    return (
      <div style={{ padding: 40 }}>
        <Empty
          description={
            <>
              <div>FRAML scoring is not enabled on this deployment.</div>
              <div style={{ color: '#8c8c8c', marginTop: 8 }}>
                Ask ops to set <code>TOOKITAKI_FRAML_SCORING_ENABLED=true</code>{' '}
                on the amls-backend and cm-backend helm values.
              </div>
            </>
          }
        />
      </div>
    );
  }

  const p = state.policy;
  return (
    <div style={styles.page}>
      {/* Page header — version + effective window + Kafka-broadcast note */}
      <div style={styles.pageHeader}>
        <div>
          <div style={styles.pageTitle}>Active FRAML decay policy</div>
          <div style={styles.pageSubtitle}>
            Every scoring pipeline downstream — case anchors, live consolidator
            snapshots, batch precompute — feeds off this row. A save publishes a
            Kafka broadcast so consolidator pipelines hot-reload without a
            restart.
          </div>
        </div>
        <div style={styles.versionPill}>
          <Tag color="geekblue" style={styles.versionTag}>
            {p.policyVersionId}
          </Tag>
          <div style={styles.versionDates}>
            Effective from <strong>{fmtDate(p.effectiveFrom)}</strong>
            {p.effectiveTo ? (
              <>
                {' '}
                until <strong>{fmtDate(p.effectiveTo)}</strong>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={pickScalars(p)}
        onFinish={onSubmit}>
        {/* FIN-12421 UI polish — the four editable sub-cards live inside a responsive
            2-column grid (single column below 900px) so the page fills the horizontal
            real estate that used to render as empty gray space. Header card + action
            row stay full-width above/below. */}
        <div style={styles.cardGrid}>
          {/* Card 1 — Scoring weights (Σ = 1 constraint) */}
          <SectionCard
            title="Scoring weights"
            hint="Convex combination of the AML and Fraud terms in the final FRAML score. w1_aml + w2_fraud must equal 1 — editing one auto-updates the other."
            data-testid="framl-settings-weights-card">
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="w1Aml"
                  label={
                    <FieldLabel
                      text="AML weight (w1_aml)"
                      hint="Coefficient on the customer's latest AML_TM score."
                    />
                  }
                  rules={[
                    { required: true, message: 'Required' },
                    {
                      type: 'number',
                      min: 0,
                      max: 1,
                      message: 'Must be between 0 and 1'
                    },
                    sumToOneRule(form)
                  ]}>
                  <InputNumber
                    step={0.01}
                    min={0}
                    max={1}
                    precision={2}
                    style={{ width: '100%' }}
                    onChange={(v) => couplePartner(form, 'w1Aml', 'w2Fraud', v)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="w2Fraud"
                  label={
                    <FieldLabel
                      text="Fraud weight (w2_fraud)"
                      hint="Coefficient on the customer's normalised fraud heat (sigmoid of decayed contributions)."
                    />
                  }
                  dependencies={['w1Aml']}
                  rules={[
                    { required: true, message: 'Required' },
                    {
                      type: 'number',
                      min: 0,
                      max: 1,
                      message: 'Must be between 0 and 1'
                    },
                    sumToOneRule(form)
                  ]}>
                  <InputNumber
                    step={0.01}
                    min={0}
                    max={1}
                    precision={2}
                    style={{ width: '100%' }}
                    onChange={(v) => couplePartner(form, 'w2Fraud', 'w1Aml', v)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <WeightsSumIndicator form={form} />
          </SectionCard>

          {/* Card 2 — Time-decay */}
          <SectionCard
            title="Time-decay"
            hint="Older alerts fade via λ = ln(2) / base_half_life_hours. The load window bounds how far back contributions are read."
            data-testid="framl-settings-decay-card">
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="baseHalfLifeHours"
                  label={
                    <FieldLabel
                      text="Base half-life (hours)"
                      hint="Time for an alert's contribution to halve, before per-alert multipliers are applied."
                    />
                  }
                  dependencies={['loadWindowHours']}
                  rules={[
                    { required: true, message: 'Required' },
                    { type: 'number', min: 0.01, message: 'Must be positive' },
                    ratioSanityCheckRule(form)
                  ]}>
                  <InputNumber
                    step={1}
                    min={0.01}
                    style={{ width: '100%' }}
                    addonAfter="hrs"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="loadWindowHours"
                  label={
                    <FieldLabel
                      text="Load window (hours)"
                      hint="Bound on how far back the consolidator + anchor writer scan unified_alerts."
                    />
                  }
                  dependencies={['baseHalfLifeHours']}
                  rules={[
                    { required: true, message: 'Required' },
                    {
                      type: 'number',
                      min: 1,
                      message: 'Must be at least 1 hour'
                    },
                    ratioSanityCheckRule(form)
                  ]}>
                  <InputNumber
                    step={1}
                    min={1}
                    style={{ width: '100%' }}
                    addonAfter="hrs"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>

          {/* Card — FRAML Risk Band Bounds (FIN-12421 perf follow-up).
            FIN-13794 (Q10 revised — 2026-09-30 product lock): three MIN-oriented inputs.
            Admin enters Low / Good / High band MINs; storage stays `{framlRiskBandLowMin,
            framlRiskBandLowMax, framlRiskBandGoodMax}` for FQ3 backward-compat. The three
            hidden Form.Items keep the wire fields and their required + range rules; the
            visible block above them reads/writes those fields via form.setFieldsValue after
            translating GoodMin/HighMin → framlRiskBandLowMax/GoodMax via -1. */}
          <SectionCard
            title="FRAML risk-band bounds"
            hint="Cutoffs that map a customer's FRAML score to LOW / GOOD / HIGH bands on the FRAML customer detail + queue views. Scenario alert-level bands are configured separately."
            data-testid="framl-settings-risk-band-card">
            <Form.Item shouldUpdate noStyle>
              {() => {
                const lowMin = Number(form.getFieldValue('framlRiskBandLowMin') ?? 0);
                const lowMax = Number(form.getFieldValue('framlRiskBandLowMax') ?? 33);
                const goodMax = Number(form.getFieldValue('framlRiskBandGoodMax') ?? 66);
                const goodMin = lowMax + 1;
                const highMin = goodMax + 1;
                const invalid =
                  !(lowMin < goodMin) || !(goodMin < highMin) || !(highMin <= 100);
                return (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 14,
                        marginBottom: 12,
                        fontSize: 12,
                        color: '#4b5769'
                      }}>
                      <BandChip color="#94a3b8">
                        Suppressed: 0 – {Math.max(0, lowMin - 1)}
                      </BandChip>
                      <BandChip color="#1f9d5b">
                        Low: {lowMin} – {lowMax}
                      </BandChip>
                      <BandChip color="#b9770e">
                        Good: {goodMin} – {goodMax}
                      </BandChip>
                      <BandChip color="#d6453a">
                        High: {highMin} – 100
                      </BandChip>
                    </div>
                    <Row gutter={20}>
                      <Col xs={24} md={8}>
                        <FieldLabel
                          text="Low ≥"
                          hint="Score at which the LOW band starts. Sub-Low scores are suppressed (customer hidden from queue). 0 = no suppression."
                        />
                        <InputNumber
                          data-testid="framl-risk-band-low-min"
                          min={0}
                          max={100}
                          step={1}
                          precision={0}
                          style={{ width: '100%' }}
                          value={lowMin}
                          onChange={(v) =>
                            typeof v === 'number' &&
                            form.setFieldsValue({ framlRiskBandLowMin: v })
                          }
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <FieldLabel
                          text="Good ≥"
                          hint="Score at which the GOOD band starts. Storage keeps this as (GoodMin - 1) on framlRiskBandLowMax for backward-compat."
                        />
                        <InputNumber
                          data-testid="framl-risk-band-good-min"
                          min={1}
                          max={100}
                          step={1}
                          precision={0}
                          style={{ width: '100%' }}
                          value={goodMin}
                          onChange={(v) =>
                            typeof v === 'number' &&
                            form.setFieldsValue({ framlRiskBandLowMax: v - 1 })
                          }
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <FieldLabel
                          text="High ≥"
                          hint="Score at which the HIGH band starts. Storage keeps this as (HighMin - 1) on framlRiskBandGoodMax for backward-compat."
                        />
                        <InputNumber
                          data-testid="framl-risk-band-high-min"
                          min={2}
                          max={100}
                          step={1}
                          precision={0}
                          style={{ width: '100%' }}
                          value={highMin}
                          onChange={(v) =>
                            typeof v === 'number' &&
                            form.setFieldsValue({ framlRiskBandGoodMax: v - 1 })
                          }
                        />
                      </Col>
                    </Row>
                    {invalid && (
                      <div
                        role="alert"
                        style={{
                          marginTop: 8,
                          color: '#d6453a',
                          fontSize: 12
                        }}>
                        Bands must satisfy 0 ≤ Low MIN &lt; Good MIN &lt; High MIN ≤ 100.
                      </div>
                    )}
                  </div>
                );
              }}
            </Form.Item>
            {/* Hidden Form.Items — keep the wire fields (framlRiskBandLowMin/LowMax/GoodMax)
                in the form state so the existing required + range + ordering rules still fire
                on submit. */}
            <Form.Item
              name="framlRiskBandLowMin"
              hidden
              rules={[
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
                riskBandOrderingRule(form)
              ]}
            />
            <Form.Item
              name="framlRiskBandLowMax"
              hidden
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
                riskBandOrderingRule(form)
              ]}
            />
            <Form.Item
              name="framlRiskBandGoodMax"
              hidden
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
                riskBandOrderingRule(form)
              ]}
            />
          </SectionCard>

          {/* Card 3 — Sigmoid curve */}
          <SectionCard
            title="Sigmoid normalisation"
            hint="Maps raw fraud heat to a bounded [0, 100] value via 100 · 1 / (1 + exp(-k · (heat - midpoint)))."
            data-testid="framl-settings-sigmoid-card">
            <Row gutter={20}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sigmoidK"
                  label={
                    <FieldLabel
                      text="Sigmoid steepness (k)"
                      hint="Controls how quickly the curve saturates near 0 and 100."
                    />
                  }
                  rules={[
                    { required: true, message: 'Required' },
                    { type: 'number', min: 0.0001, message: 'Must be positive' }
                  ]}>
                  <InputNumber
                    step={0.01}
                    min={0.0001}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="sigmoidMidpoint"
                  label={
                    <FieldLabel
                      text="Sigmoid midpoint"
                      hint="Raw-heat value that maps to the 50 mark on the normalised scale."
                    />
                  }
                  rules={[
                    { required: true, message: 'Required' },
                    { type: 'number', min: 0, message: 'Must be non-negative' }
                  ]}>
                  <InputNumber step={1} min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </SectionCard>
        </div>
        {/* end .cardGrid */}

        {/* Sticky-looking action row */}
        <div style={styles.actionRow}>
          <div style={styles.actionHint}>
            Publishing takes effect immediately across every consolidator
            pipeline.
          </div>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={saving}>
            Save
          </Button>
        </div>
      </Form>

      {/*
        FIN-12421 — "Advanced — severity + multiplier maps" section intentionally
        hidden from the settings UI for now. The maps are read-only in this build
        (edited via ops migrations on framl_decay_policy) and exposing them without
        a policy-versioning UX confuses users. Re-enable when the FE learns to edit
        them and the BE writer supports partial-map updates. The values still
        round-trip through the write path via `merged = { ...state.policy, ... }`
        so hiding the section does NOT drop them from the DTO.
      */}

      <Modal
        title="Publish FRAML settings?"
        visible={confirmVisible}
        onOk={handleConfirmedSave}
        onCancel={() => setConfirmVisible(false)}
        okText="Publish"
        cancelText="Cancel">
        <p>
          Saving updates the FRAML decay policy immediately and broadcasts to
          all downstream consolidator pipelines. This affects production scoring
          behaviour.
        </p>
        <p>Continue?</p>
      </Modal>
    </div>
  );
};

// ── Small presentational helpers ─────────────────────────────────────────────

const styles = {
  page: {
    padding: '24px 32px 48px',
    // FIN-12421 UI polish: widened from 1080 → 1440 and switched to `margin: 0 auto` so the
    // page uses the horizontal space that previously rendered as empty gray on the right.
    maxWidth: 1440,
    margin: '0 auto',
    fontFamily: "'Inter', system-ui, sans-serif",
    color: '#29303d'
  },
  // FIN-12421 UI polish: 2-column responsive grid for the four editable sub-cards.
  // Uses CSS auto-fit so on narrow viewports (< ~900 px) the cards collapse to a single
  // column with no wasted whitespace, and on wide viewports the four cards render as a
  // 2 × 2 grid — matches the Default Scoring page's information density.
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    gap: 20,
    marginBottom: 8
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    padding: '20px 24px',
    background: 'linear-gradient(180deg,#eef4ff 0%,#f6f9ff 100%)',
    border: '1px solid #d6e2ff',
    borderRadius: 12,
    marginBottom: 24
  },
  pageTitle: { fontSize: 20, fontWeight: 600, color: '#1e2a3d' },
  pageSubtitle: {
    marginTop: 6,
    color: '#4b5769',
    fontSize: 13,
    maxWidth: 720,
    lineHeight: 1.55
  },
  versionPill: {
    textAlign: 'right',
    minWidth: 240
  },
  versionTag: {
    fontSize: 12,
    fontWeight: 500,
    padding: '2px 10px',
    marginRight: 0,
    marginBottom: 4
  },
  versionDates: { color: '#4b5769', fontSize: 12 },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '16px 20px',
    background: '#fff',
    border: '1px solid #e6e9ef',
    borderRadius: 10,
    marginTop: 8,
    marginBottom: 32
  },
  actionHint: { color: '#676f7e', fontSize: 13 }
};

const SectionCard = ({ title, hint, children, toneMuted, ...rest }) => (
  <div
    {...rest}
    style={{
      background: '#fff',
      border: '1px solid #e6e9ef',
      borderRadius: 12,
      padding: '18px 22px 4px',
      // FIN-12421 UI polish — margin removed; the parent `.cardGrid` uses `gap` so cards
      // space out uniformly on all sides in either single- or two-column layouts.
      boxShadow: toneMuted ? 'none' : '0 1px 2px rgba(20,25,35,0.03)'
    }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 6
      }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1e2a3d' }}>
        {title}
      </div>
    </div>
    {hint && (
      <div
        style={{
          color: '#676f7e',
          fontSize: 12.5,
          marginBottom: 14,
          lineHeight: 1.5
        }}>
        {hint}
      </div>
    )}
    {children}
  </div>
);

const FieldLabel = ({ text, hint }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span>{text}</span>
    {hint && (
      <Tooltip title={hint}>
        <InfoCircleOutlined style={{ color: '#8c95a6', fontSize: 12 }} />
      </Tooltip>
    )}
  </span>
);

// FIN-12421 — `MapTable` component removed with the hidden Advanced section.
// If/when we re-expose severity + multiplier maps to the UI, restore it from
// git history (previously lived here).

SectionCard.propTypes = {
  title: PropTypes.node.isRequired,
  hint: PropTypes.node,
  children: PropTypes.node,
  toneMuted: PropTypes.bool
};

FieldLabel.propTypes = {
  text: PropTypes.node.isRequired,
  hint: PropTypes.node
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return iso;
  }
};

const pickScalars = (p) => ({
  w1Aml: p.w1Aml,
  w2Fraud: p.w2Fraud,
  baseHalfLifeHours: p.baseHalfLifeHours,
  sigmoidK: p.sigmoidK,
  sigmoidMidpoint: p.sigmoidMidpoint,
  loadWindowHours: p.loadWindowHours,
  // FIN-12421 (perf follow-up) — FRAML risk-band cutoffs, defaults 33 / 66 for BE compat on
  // pre-migration deploys where the columns may not yet exist in the response.
  framlRiskBandLowMax:
    p.framlRiskBandLowMax != null ? p.framlRiskBandLowMax : 33,
  framlRiskBandGoodMax:
    p.framlRiskBandGoodMax != null ? p.framlRiskBandGoodMax : 66,
  // FIN-13794 — FRAML risk-band LOW-MIN cutoff. Below this score the customer has no band
  // (hidden from queue). Optional field; null on legacy deployments where the column does
  // not exist yet → default 0 preserves today's behaviour.
  framlRiskBandLowMin:
    p.framlRiskBandLowMin != null ? p.framlRiskBandLowMin : 0
});

// FIN-12421 — coupled slider for the two FRAML risk-band cutoffs.
// Ant Design Range slider with two handles: [low_max, good_max]. Rendered with
// green/orange/red gradient tracks so LOW / GOOD / HIGH zones read at a glance.
const ScoreBandsSlider = ({ low, good, onChange }) => {
  const safeLow = clamp(low, 0, 100, 33);
  const safeGood = clamp(good, safeLow, 100, 66);
  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 14,
          marginBottom: 8,
          fontSize: 12,
          color: '#4b5769'
        }}>
        <BandChip color="#1f9d5b">Low: 0 – {safeLow}</BandChip>
        <BandChip color="#b9770e">
          Good: {safeLow} – {safeGood}
        </BandChip>
        <BandChip color="#d6453a">High: {safeGood} – 100</BandChip>
      </div>
      <Slider
        range
        min={0}
        max={100}
        step={1}
        value={[safeLow, safeGood]}
        onChange={(next) => {
          const [a, b] = next;
          // Enforce a >= 0, b <= 100, a <= b (Ant Slider already does this but be defensive).
          const nextLow = clamp(a, 0, 100, safeLow);
          const nextGood = clamp(b, nextLow, 100, safeGood);
          onChange([nextLow, nextGood]);
        }}
      />
    </div>
  );
};

ScoreBandsSlider.propTypes = {
  low: PropTypes.number,
  good: PropTypes.number,
  onChange: PropTypes.func.isRequired
};

const BandChip = ({ color, children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '2px 8px',
      borderRadius: 12,
      background: '#f6f9ff',
      border: `1px solid ${color}33`,
      color: '#29303d'
    }}>
    <span
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: 3,
        background: color
      }}
    />
    {children}
  </span>
);

BandChip.propTypes = {
  color: PropTypes.string.isRequired,
  children: PropTypes.node
};

function clamp(v, lo, hi, fallback) {
  const n = Number(v);
  if (Number.isNaN(n)) return fallback;
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

// FIN-13794 (Q13 + FQ4 + adversarial review P2-V3) — strict-less-than ordering enforced on the
// three cutoffs in the storage-shape fields. `0 <= framlRiskBandLowMin < framlRiskBandLowMax <
// framlRiskBandGoodMax < 100`. `< 100` (strict) because under FQ4's `<` on derived MINs
// (`highMin = framlRiskBandGoodMax + 1`) a GoodMax = 100 empties the HIGH band.
const riskBandOrderingRule = (form) => ({
  validator: async () => {
    const min = Number(form.getFieldValue('framlRiskBandLowMin'));
    const lo = Number(form.getFieldValue('framlRiskBandLowMax'));
    const hi = Number(form.getFieldValue('framlRiskBandGoodMax'));
    if (Number.isNaN(lo) || Number.isNaN(hi)) return;
    if (!Number.isNaN(min) && min < 0) {
      throw new Error('Low band minimum cannot be negative.');
    }
    if (!Number.isNaN(min) && min >= lo) {
      throw new Error(
        'Low band minimum must be strictly less than Low band maximum ' +
          '(sub-lowMin scores are suppressed and the LOW band needs non-zero width).'
      );
    }
    if (lo >= hi) {
      throw new Error(
        'Low band maximum must be strictly less than Good band maximum ' +
          '(LOW and GOOD bands need non-zero width).'
      );
    }
    if (!(hi < 100)) {
      throw new Error(
        'Good band maximum must be strictly less than 100 (HIGH band needs non-zero width; ' +
          'runtime evaluates score < mediumMax + 1).'
      );
    }
  }
});

// FIN-12421 — convex-combination guard for the two scoring weights (w1_aml + w2_fraud).
// The pair must sum to exactly 1. UX: editing one field auto-updates the partner to
// (1 - v) so the invariant holds without asking the user to keep both numbers in sync;
// the sanity indicator below the fields (WeightsSumIndicator) shows the live sum, and
// sumToOneRule blocks save if a manual edit somehow leaves the pair off by more than
// EPSILON (0.001 — allows 2dp rounding noise).
const SUM_EPSILON = 0.001;

// Round to 2dp to match the InputNumber's `precision={2}` — prevents float drift like
// 1 - 0.4 = 0.6 || 0.5999999999999999.
const round2 = (n) => Math.round(n * 100) / 100;

const couplePartner = (form, edited, partner, newValue) => {
  if (
    newValue === null ||
    newValue === undefined ||
    Number.isNaN(Number(newValue))
  )
    return;
  const clamped = Math.min(1, Math.max(0, Number(newValue)));
  form.setFieldsValue({ [partner]: round2(1 - clamped) });
};

const sumToOneRule = (form) => ({
  validator: async () => {
    const w1 = Number(form.getFieldValue('w1Aml'));
    const w2 = Number(form.getFieldValue('w2Fraud'));
    if (Number.isNaN(w1) || Number.isNaN(w2)) return;
    if (Math.abs(w1 + w2 - 1) > SUM_EPSILON) {
      throw new Error(
        `w1_aml + w2_fraud must equal 1 (current sum: ${(w1 + w2).toFixed(2)}).`
      );
    }
  }
});

const WeightsSumIndicator = ({ form }) => {
  // Re-read on every parent render — Form's shouldUpdate wrapper triggers re-render on any field change.
  return (
    <Form.Item shouldUpdate noStyle>
      {() => {
        const w1 = Number(form.getFieldValue('w1Aml'));
        const w2 = Number(form.getFieldValue('w2Fraud'));
        if (Number.isNaN(w1) || Number.isNaN(w2)) return null;
        const sum = w1 + w2;
        const ok = Math.abs(sum - 1) <= SUM_EPSILON;
        return (
          <div
            data-testid="framl-settings-weights-sum"
            style={{
              marginTop: -6,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: ok ? '#237804' : '#a8071a',
              fontSize: 12.5
            }}>
            <Tag
              color={ok ? 'green' : 'red'}
              style={{ margin: 0, fontFamily: 'monospace' }}>
              Σ = {sum.toFixed(2)}
            </Tag>
            <span>
              {ok
                ? 'Weights sum to 1 — good.'
                : 'Weights must sum to exactly 1. Adjust either field; the partner auto-updates.'}
            </span>
          </div>
        );
      }}
    </Form.Item>
  );
};

WeightsSumIndicator.propTypes = {
  // Ant Design FormInstance — opaque object.
  form: PropTypes.object.isRequired
};

// FIN-12421 Item 7 — sanity-check the loadWindow-to-half-life ratio. Independent knobs
// in the schema, but a well-configured system keeps loadWindow between ~5× and ~100× the
// half-life:
//   - < 5× → contributions get truncated before they've decayed to a negligible level,
//            so the fraud_heat_raw sum under-counts real risk.
//   - > 100× → contributions past ~10× half-life are already negligible (2^-10 ≈ 0.1%),
//              so the extra memory in ListState is wasted.
// Rendered as antd warnings (`warningOnly: true`) — the save proceeds; the operator just
// sees an inline hint. Attached to BOTH fields so editing either re-validates the pair.
const ratioSanityCheckRule = (form) => ({
  warningOnly: true,
  validator: async () => {
    const half = form.getFieldValue('baseHalfLifeHours');
    const win = form.getFieldValue('loadWindowHours');
    if (!half || !win) return;
    const ratio = win / half;
    if (ratio < 5) {
      throw new Error(
        `Load window is ${ratio.toFixed(1)}× half-life — ` +
          'contributions may be truncated before decaying ' +
          '(recommend ≥ 5×).'
      );
    }
    if (ratio > 100) {
      throw new Error(
        `Load window is ${ratio.toFixed(0)}× half-life — ` +
          'most contributions will be negligible, wasting memory ' +
          '(recommend ≤ 100×).'
      );
    }
  }
});

export default FramlSettings;
